import React from 'react'
import { Box, LinearProgress, Typography } from '@mui/material'
import { ChartBarIcon } from '@heroicons/react/24/outline'
import { PageHeader } from '../../../../shared/components/PageHeader'
import { FeedbackEmptyState } from '../../../feedbackGenerator/components'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../constants/colors'
import { getGlassPanelSurfaceStyles } from '../../../feedbackGenerator/helpers/sectionCardStyles'
import { useTranslation } from '@/shared/hooks/useIntractionTranslate'

export type LatestSessionFeedbackStatePanelVariant = 'apiError' | 'generationFailed' | 'generating' | 'empty'

export interface LatestSessionFeedbackStatePanelProps {
  variant: LatestSessionFeedbackStatePanelVariant
  /** 0–100 when variant === generating */
  progress?: number
}

const panelSx = {
  minHeight: { xs: '320px', sm: '360px', md: '400px' },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  px: { xs: 2, sm: 3, md: 6 },
  py: { xs: 3, sm: 4, md: 6 },
  ...getGlassPanelSurfaceStyles('individual')
} as const

/**
 * Shared glass panel for loading-adjacent states on the latest session feedback route.
 */
export const LatestSessionFeedbackStatePanel: React.FC<LatestSessionFeedbackStatePanelProps> = ({
  variant,
  progress = 0
}) => {
  const { t } = useTranslation()

  const header = (
    <PageHeader
      badgeText={t('gamer.feedback.pageHeader.badge')}
      badgeIcon={<ChartBarIcon />}
      title={t('gamer.feedback.pageHeader.title')}
      subtitle={t('gamer.feedback.pageHeader.subtitle')}
    />
  )

  const shell = (children: React.ReactNode) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: { xs: 2, sm: 3, md: 3 }
      }}
    >
      {header}
      {children}
    </Box>
  )

  if (variant === 'empty') {
    return shell(
      <Box
        sx={{
          ...panelSx,
          borderRadius: '10px',
          border: `1px solid ${INTERAX_COLORS.white04}`,
          boxShadow: `inset 0px 1px 0px ${INTERAX_COLORS.white08}`
        }}
      >
        <FeedbackEmptyState />
      </Box>
    )
  }

  if (variant === 'generating') {
    return shell(
      <Box sx={panelSx}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 2, sm: 2.5, md: 3 },
            textAlign: 'center',
            width: '100%',
            maxWidth: '500px'
          }}
        >
          <Typography sx={{ ...INTERAX_TYPOGRAPHY.headline4, color: INTERAX_COLORS.white }}>
            {t('gamer.feedback.generating.title', 'Your report is being generated')}
          </Typography>
          <Typography
            sx={{
              ...INTERAX_TYPOGRAPHY.body2,
              color: INTERAX_COLORS.typographyFont,
              maxWidth: '500px',
              lineHeight: { xs: '22px', sm: '23px', md: '24px' }
            }}
          >
            {t(
              'gamer.feedback.generating.description',
              'This can take a while, you can get a coffee while it generates'
            )}
          </Typography>
          <Box sx={{ width: '100%', maxWidth: '400px', mt: { xs: 1, sm: 2, md: 2 } }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: '8px',
                borderRadius: '4px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: INTERAX_COLORS.sun,
                  borderRadius: '4px'
                }
              }}
            />
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.body2,
                color: INTERAX_COLORS.white,
                textAlign: 'center',
                mt: 1
              }}
            >
              {Math.round(progress)}%
            </Typography>
          </Box>
        </Box>
      </Box>
    )
  }

  const messageKey =
    variant === 'apiError' ? 'gamer.feedback.error.technicalProblems' : 'gamer.feedback.error.generationFailed'
  const messageDefault =
    variant === 'apiError'
      ? 'We are having technical problems please try again later, or contact us'
      : 'We are having issues generating report at this time'

  return shell(
    <Box sx={panelSx}>
      <Typography sx={{ ...INTERAX_TYPOGRAPHY.headline4, color: INTERAX_COLORS.white, textAlign: 'center' }}>
        {t(messageKey, messageDefault)}
      </Typography>
    </Box>
  )
}
